import {
  deduplicateCitations,
  extractCitationsFromContent,
  sortCitationsByNumber,
} from '@/lib/utils/citationExtractor';
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { setCitationsForSession } from '../slices/citationsSlice';
import {
  addMessage,
  addMessageBatch,
  chatHistorySelectors,
  completeStreamingMessage,
  initializeSession,
  updateMessage,
} from '../slices/researchSessionSlice';
import type { AppDispatch, RootState } from '../store';

// Create typed listener middleware
export const citationExtractionListener = createListenerMiddleware();

// Get typed start/add functions
const startCitationListening =
  citationExtractionListener.startListening.withTypes<RootState, AppDispatch>();

// Debounce tracking to prevent excessive extractions during rapid streaming
let lastExtractionTime = 0;
const EXTRACTION_DEBOUNCE_MS = 100; // Minimum time between extractions

// Listen to all actions that modify chat history
// PR #877 (closes #852): Also listen to completeStreamingMessage to extract citations
// when streaming finishes for non-legal queries using the general knowledge/web-search fallback
startCitationListening({
  matcher: isAnyOf(
    initializeSession,
    addMessage,
    addMessageBatch,
    updateMessage,
    completeStreamingMessage
  ),
  effect: async (action, listenerApi) => {
    const perfStart = performance.now();

    // Debounce rapid extractions (e.g., during streaming)
    const now = Date.now();
    const timeSinceLastExtraction = now - lastExtractionTime;
    if (timeSinceLastExtraction < EXTRACTION_DEBOUNCE_MS) {
      // Skip if we just extracted recently (except for complete/batch actions which are important)
      const isImportantAction =
        action.type === completeStreamingMessage.type ||
        action.type === addMessageBatch.type ||
        action.type === initializeSession.type;
      if (!isImportantAction) {
        return;
      }
    }
    lastExtractionTime = now;

    // Small delay to ensure state is fully updated
    await listenerApi.delay(10);

    // Get current state after action processed
    const state = listenerApi.getState();
    const sessionId = state.researchSession.sessionId;

    // Early exit if no session
    if (!sessionId) {
      return;
    }

    // Skip if citations already exist for this session (e.g., from citation_metadata SSE event)
    const existingCitations = state.citations.citationsBySession[sessionId];
    if (existingCitations?.length && action.type === initializeSession.type) {
      return;
    }

    // Get locale from citations slice (set by app on initialization)
    // Fallback to 'sk' if not set
    const locale = state.citations.locale || 'sk';

    // Get all chat history messages
    const allMessages = chatHistorySelectors.selectAll(state);

    // Extract citations from all assistant messages
    const allCitations: ReturnType<typeof extractCitationsFromContent> = [];
    let messagesProcessed = 0;

    allMessages.forEach((item) => {
      if (item.role !== 'user' && item.content) {
        try {
          const citations = extractCitationsFromContent(item.content, locale);
          messagesProcessed++;
          if (citations.length > 0) {
            allCitations.push(...citations);
          }
        } catch (error) {
          console.error(
            '[Citation Extraction Listener] Error extracting from message:',
            error
          );
        }
      }
    });

    // Deduplicate and sort
    const uniqueCitations = deduplicateCitations(allCitations);
    const sortedCitations = sortCitationsByNumber(uniqueCitations);

    // Update citations in Redux
    listenerApi.dispatch(
      setCitationsForSession({
        sessionId,
        citations: sortedCitations,
      })
    );

    const perfEnd = performance.now();
    const extractionTime = Math.round(perfEnd - perfStart);

    // Performance logging with warning for slow extractions
    if (extractionTime > 100) {
      console.warn(
        `⚠️ [Citation Extraction Listener] SLOW EXTRACTION: ${extractionTime}ms for ${messagesProcessed} messages → ${sortedCitations.length} citations`
      );
    } else {
    }
  },
});
